import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import { reviewedLiabilityValuesFromCase, type Task } from "@recobra/schemas/task";

import { AiSuggestionStatus } from "../../cases/ai-review";
import { RequirementChecklist } from "../requirement-checklist";
import type { TaskMetadataEditor } from "../task-details";
import type { TaskRequirementContext } from "../task-details";
import { TaskModalSection } from "../task-display";
import { LiabilityReviewedValues } from "./LiabilityReviewedValues";
import {
  LiabilityDeclineReasonSelect,
  NextTaskSelect,
  SolutionSelect,
  TaskCompletionNotes,
} from "./TaskExecutionInputs";
import type { TaskExecutionPolicy, useTaskExecutionDraft } from "./useTaskExecutionDraft";

export const TaskExecutionFields: FC<{
  task: Task;
  draft: ReturnType<typeof useTaskExecutionDraft>;
  context: TaskRequirementContext;
  policy: TaskExecutionPolicy;
  canWaive: boolean;
  editor: TaskMetadataEditor;
  onSatisfy: (requirementKey: string, satisfied: boolean) => void;
  onWaive: (requirementKey: string, waived: boolean) => void;
  isSubmitting: boolean;
  onNavigate?: () => void;
}> = ({
  task,
  draft,
  context,
  policy,
  canWaive,
  editor,
  onSatisfy,
  onWaive,
  isSubmitting,
  onNavigate,
}) => {
  return (
    <>
      <RequirementChecklist
        task={task}
        flow={context.taskType}
        resolutionId={draft.selectedResolution?.id ?? null}
        canWaive={canWaive}
        editor={editor}
        disabled={isSubmitting}
        handlers={{ onSatisfy, onWaive, onNavigate }}
      />

      <LiabilityReviewedValues
        taskType={context.taskType}
        values={reviewedLiabilityValuesFromCase(context.parent)}
      />

      <TaskExecutionControls
        task={task}
        draft={draft}
        context={context}
        policy={policy}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

const TaskExecutionControls: FC<{
  task: Task;
  draft: ReturnType<typeof useTaskExecutionDraft>;
  context: TaskRequirementContext;
  policy: TaskExecutionPolicy;
  isSubmitting: boolean;
}> = ({ task, draft, context, policy, isSubmitting }) => {
  const { showSolutionSelect, showCompletionNotes, showAiAnalysis, suggestedResolution, visible } =
    controlsVisibility(task, draft, context);
  if (!visible) return null;

  return (
    <TaskModalSection>
      {showAiAnalysis && task.aiAnalysis ? (
        <AiSuggestionStatus
          value={suggestedResolution?.label}
          reasoning={task.aiAnalysis.reasoning}
          status={task.aiAnalysis.status}
        />
      ) : null}

      {showSolutionSelect ? (
        <SolutionSelect
          id={`${task.id}-solution`}
          resolutions={draft.options}
          value={draft.selectedValue}
          onChange={draft.selectSolution}
          disabled={isSubmitting || policy.mode === "locked"}
        />
      ) : null}
      {draft.nextTasks.length ? (
        <NextTaskSelect
          id={`${task.id}-next-task`}
          options={draft.nextTasks}
          value={draft.nextTaskType}
          onChange={draft.selectNextTask}
          disabled={isSubmitting}
        />
      ) : null}
      {draft.needsDeclineReason ? (
        <LiabilityDeclineReasonSelect
          id={`${task.id}-decline-reason`}
          value={draft.declineReason}
          onChange={draft.setDeclineReason}
          disabled={isSubmitting}
        />
      ) : null}
      {context.isLoading ? (
        <p className="text-xs text-muted-foreground">{m.task_execution_context_loading()}</p>
      ) : null}

      {showCompletionNotes && (
        <TaskCompletionNotes
          notes={draft.notes}
          onNotesChange={draft.setNotes}
          saveStatus={draft.notesSaveStatus}
          solution={draft.selectedValue}
          disabled={isSubmitting}
        />
      )}
    </TaskModalSection>
  );
};

/** Which execution controls apply to the task and draft; nothing renders when none does. */
const controlsVisibility = (
  task: Task,
  draft: ReturnType<typeof useTaskExecutionDraft>,
  context: TaskRequirementContext,
) => {
  const showSolutionSelect = draft.options.length > 1 && task.system !== "provide_case_documents";
  const showCompletionNotes = context.taskType?.collectsCompletionNotes !== false;
  const suggestedResolution = draft.options.find(
    ({ id }) => id === task.aiAnalysis?.suggestedSolution,
  );
  const showAiAnalysis = Boolean(
    task.aiAnalysis && (task.aiAnalysis.status !== "completed" || suggestedResolution),
  );
  const visible = [
    showAiAnalysis,
    showSolutionSelect,
    draft.nextTasks.length > 0,
    draft.needsDeclineReason,
    context.isLoading,
    showCompletionNotes,
  ].some(Boolean);
  return { showSolutionSelect, showCompletionNotes, showAiAnalysis, suggestedResolution, visible };
};
